import { ENV } from "@/config/env";

export interface AuthAccount {
  id: string;
  username: string;
  email?: string;
  givenName?: string;
  surname?: string;
  name?: string;
  roles?: string[];
}

export interface AssistantSession {
  isAuthenticated: true;
  userId: string;
  name?: string;
  email?: string;
  provider?: string;
  roles: string[];
  account: AuthAccount;
}

export interface AssistantCsrfToken {
  headerName: string;
  token: string;
}

const LOGOUT_IN_PROGRESS_STORAGE_KEY = "assistant-portal.auth.logout-in-progress";
const ANTIFORGERY_FORM_FIELD_NAME = "__RequestVerificationToken";
const THEME_STORAGE_KEY = "vite-ui-theme";

let cachedSession: AssistantSession | null = null;
let cachedCsrfToken: AssistantCsrfToken | null = null;
let csrfPromise: Promise<AssistantCsrfToken | null> | null = null;

const ensureBrowser = (): void => {
  if (typeof window === "undefined") {
    throw new Error("Assistant authentication is only available in the browser.");
  }
};

const getAssistantApiBaseUrl = (): string => {
  const baseUrl = ENV.ASSISTANT_GRAPHQL_API_URL?.trim();
  if (!baseUrl) {
    throw new Error("Missing Assistant GraphQL API URL.");
  }

  return baseUrl.replace(/\/+$/, "");
};

const buildBffUrl = (path: string): URL => {
  return new URL(path, `${getAssistantApiBaseUrl()}/`);
};

const submitNavigationForm = (action: URL, fields: Record<string, string>): void => {
  ensureBrowser();

  const form = window.document.createElement("form");
  form.method = "POST";
  form.action = action.toString();
  form.style.display = "none";

  Object.entries(fields).forEach(([name, value]) => {
    const input = window.document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  window.document.body.appendChild(form);
  form.submit();
};

const normalizeReturnUrl = (redirectTo?: string): string => {
  ensureBrowser();

  if (!redirectTo) {
    return window.location.href;
  }

  if (redirectTo.startsWith("/")) {
    return new URL(redirectTo, window.location.origin).toString();
  }

  return redirectTo;
};

const getPostLogoutRedirectUri = (): string => {
  ensureBrowser();
  return new URL("/logout/callback", window.location.origin).toString();
};

const getAssistantLoginTheme = (): "dark" | "light" => {
  ensureBrowser();

  const root = window.document.documentElement;
  if (root.classList.contains("dark")) {
    return "dark";
  }

  if (root.classList.contains("light")) {
    return "light";
  }

  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === "dark" || storedTheme === "light") {
      return storedTheme;
    }
  } catch {
    // Ignore storage access failures and fall back to the system preference.
  }

  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
};

const setLogoutInProgress = (): void => {
  ensureBrowser();
  window.sessionStorage.setItem(LOGOUT_IN_PROGRESS_STORAGE_KEY, "true");
};

const buildAccount = (session: Omit<AssistantSession, "account">): AuthAccount => {
  const username = session.email?.trim() || session.name?.trim() || session.userId;
  const [givenName, ...surnameParts] = session.name?.trim().split(/\s+/) ?? [];

  return {
    id: session.userId,
    username,
    email: session.email,
    givenName,
    surname: surnameParts.length > 0 ? surnameParts.join(" ") : undefined,
    name: session.name,
    roles: session.roles,
  };
};

const normalizeSession = (payload: unknown): AssistantSession => {
  if (!payload || typeof payload !== "object") {
    throw new Error("Session payload was not valid JSON.");
  }

  const session = payload as {
    isAuthenticated?: boolean;
    userId?: unknown;
    name?: unknown;
    email?: unknown;
    provider?: unknown;
    roles?: unknown;
  };

  if (!session.isAuthenticated || typeof session.userId !== "string" || session.userId.trim().length === 0) {
    throw new Error("Session payload did not include an authenticated user.");
  }

  const normalized: Omit<AssistantSession, "account"> = {
    isAuthenticated: true,
    userId: session.userId,
    name: typeof session.name === "string" ? session.name : undefined,
    email: typeof session.email === "string" ? session.email : undefined,
    provider: typeof session.provider === "string" ? session.provider : undefined,
    roles: Array.isArray(session.roles) ? session.roles.filter((role): role is string => typeof role === "string") : [],
  };

  return {
    ...normalized,
    account: buildAccount(normalized),
  };
};

export const clearAssistantLogoutInProgress = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(LOGOUT_IN_PROGRESS_STORAGE_KEY);
};

export const isAssistantLogoutInProgress = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.sessionStorage.getItem(LOGOUT_IN_PROGRESS_STORAGE_KEY) === "true";
};

export const clearAssistantAuthState = (): void => {
  cachedSession = null;
  cachedCsrfToken = null;
  csrfPromise = null;
};

export const getStoredAuthSession = (): AssistantSession | null => cachedSession;

export const getCurrentAuthAccount = (): AuthAccount | null => cachedSession?.account ?? null;

export const getAssistantLoginErrorRedirectTo = (): string | null => {
  return null;
};

export const isAssistantAuthCallback = (): boolean => {
  return false;
};

export const completeAssistantLogin = async (): Promise<{ redirectTo: string } | null> => {
  return null;
};

export const getAssistantAccessToken = async (): Promise<string | null> => {
  return null;
};

export const getAssistantAccount = async (): Promise<AuthAccount | null> => {
  return cachedSession?.account ?? null;
};

export const getAssistantCsrfToken = (): AssistantCsrfToken | null => {
  return cachedCsrfToken;
};

export const ensureAssistantCsrfToken = async (): Promise<AssistantCsrfToken | null> => {
  if (cachedCsrfToken) {
    return cachedCsrfToken;
  }

  if (!csrfPromise) {
    const csrfUrl = buildBffUrl("/bff/csrf");

    csrfPromise = fetch(csrfUrl, {
      method: "GET",
      credentials: "include",
    })
      .then(async (response) => {
        if (response.status === 401) {
          clearAssistantAuthState();
          return null;
        }

        if (!response.ok) {
          throw new Error(`CSRF request failed: ${response.status}`);
        }

        const payload = (await response.json()) as Partial<AssistantCsrfToken>;
        if (typeof payload.headerName !== "string" || typeof payload.token !== "string") {
          throw new Error("CSRF response was missing the expected token payload.");
        }

        cachedCsrfToken = {
          headerName: payload.headerName,
          token: payload.token,
        };

        return cachedCsrfToken;
      })
      .finally(() => {
        csrfPromise = null;
      });
  }

  return csrfPromise;
};

export const getAssistantSession = async (): Promise<AssistantSession | null> => {
  const sessionUrl = buildBffUrl("/bff/session");
  const response = await fetch(sessionUrl, {
    method: "GET",
    credentials: "include",
  });

  if (response.status === 401) {
    clearAssistantAuthState();
    return null;
  }

  if (!response.ok) {
    throw new Error(`Session request failed: ${response.status}`);
  }

  const session = normalizeSession(await response.json());
  cachedSession = session;
  return session;
};

export const beginAssistantLogin = async ({ redirectTo }: { redirectTo?: string } = {}): Promise<void> => {
  ensureBrowser();

  clearAssistantLogoutInProgress();

  const loginUrl = buildBffUrl("/bff/login");
  loginUrl.searchParams.set("returnUrl", normalizeReturnUrl(redirectTo));
  loginUrl.searchParams.set("theme", getAssistantLoginTheme());
  window.location.assign(loginUrl.toString());
};

export const logoutAssistant = async ({ redirectTo }: { redirectTo?: string } = {}): Promise<void> => {
  ensureBrowser();

  setLogoutInProgress();

  const returnUrl = normalizeReturnUrl(redirectTo ?? getPostLogoutRedirectUri());
  const csrfToken = await ensureAssistantCsrfToken();

  if (!csrfToken) {
    clearAssistantAuthState();
    window.location.assign(returnUrl);
    return;
  }

  const logoutUrl = buildBffUrl("/bff/logout");
  logoutUrl.searchParams.set("returnUrl", returnUrl);

  clearAssistantAuthState();
  submitNavigationForm(logoutUrl, {
    [ANTIFORGERY_FORM_FIELD_NAME]: csrfToken.token,
  });
};
